import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import Base64Util from '../utils/base64-util';
import { useSelector, useDispatch } from 'react-redux';
import { setBluetooth } from '../store/speakerSlice';

// 蓝牙服务和特征值UUID
const SERVICE_UUID = "bb37a001-b922-4018-8e74-e14824b3a638";
const CHARACTERISTIC_UUID_RX = "bb37a002-b922-4018-8e74-e14824b3a638";
const CHARACTERISTIC_UUID_TX = "bb37a003-b922-4018-8e74-e14824b3a638";
let txCharacteristic = null
let messageBuffer = new Uint8Array()
const useBluetooth = () => {
    const [isConnected, setIsConnected] = useState(false);
    const [receivedMessages, setReceivedMessages] = useState([]);
    const dispatch = useDispatch();
    const bluetooth = useSelector(state => state.speaker.bluetooth);
    // 处理蓝牙断开连接
    const handleDisconnection = useCallback(() => {
        dispatch(setBluetooth(null));
        txCharacteristic = null
        setIsConnected(false);
    }, []);

    // 解析接收到的消息
    const parseMessage = useCallback((buffer) => {
        let buf = buffer;
        let result = [];
        let bytesRemaining = null;
        let cmd = null;
        let msgLen = null;
        let end = null;
        let length = buf.length;
        let i = 0;

        while (true) {
            while (!((i >= length) || (buf[i] === 250) || (buf[i] === 251))) {
                i += 1; // 跳到下一个消息的开始
            }
            bytesRemaining = length - i;
            if (bytesRemaining < 1) {
                messageBuffer = buf.slice(i);
                return result;
            }
            cmd = buf[i];
            if ((cmd === 250) && (bytesRemaining >= 3)) { // 短消息 (3字节)
                if (27 === buf[i + 1]) {
                    result.push(buf.slice(i, i + 3));
                }
                i += 3;
            } else if ((cmd === 251) && (bytesRemaining >= 5)) { // 长消息 (>= 5字节)
                msgLen = (256 * buf[i + 4]) + buf[i + 3];
                end = i + 5 + msgLen;
                if (end > length) {
                messageBuffer = buf.slice(i);
                    return result;
                }
                if (27 === buf[i + 1]) {
                    result.push(buf.slice(i, end));
                }
                i = end;
            } else {                
                messageBuffer = buf.slice(i);
                return result;
            }
        }
    }, []);

    // 处理接收到的数据
    const handleCharacteristicValueChanged = useCallback((event, callback) => {
        const value = event.target.value;
        const buffer = Base64Util.arrayBufferToBase64(value.buffer);
        const msg = Base64Util.base64ToUint8Array(buffer);
        const newBuffer = new Uint8Array([...messageBuffer, ...msg]);

        const messages = [];
        for (const msgBytes of parseMessage(newBuffer)) {
            const string = new TextDecoder().decode(msgBytes.slice(4));
            messages.push(string.replace("\x00", ""));
        }

        if (messages.length > 0 && callback) {
            callback(messages)
        }
    }, [parseMessage]);

    // 连接蓝牙设备
    const connect = useCallback(async () => {
        message.config({
            top: 70
        })
        try {
            const bluetoothDevice = await navigator.bluetooth.requestDevice({
                filters: [{ services: [SERVICE_UUID] }]
            });

            bluetoothDevice.addEventListener('gattserverdisconnected', handleDisconnection);
            const server = await bluetoothDevice.gatt.connect();
            const service = await server.getPrimaryService(SERVICE_UUID);
            txCharacteristic = await service.getCharacteristic(CHARACTERISTIC_UUID_TX);
            dispatch(setBluetooth(bluetoothDevice));
            setIsConnected(true);
            message.success('蓝牙设备连接成功');

            return true;
        } catch (error) {
            console.error('连接蓝牙设备失败:', error);
            message.error('连接蓝牙设备失败');
            return false;
        }
    }, [handleDisconnection, handleCharacteristicValueChanged]);

    // 断开蓝牙连接
    const disconnect = useCallback(async () => {
        if (bluetooth && bluetooth.gatt.connected) {
            if (txCharacteristic) {
                await txCharacteristic.stopNotifications();
                txCharacteristic = null
            }
            await bluetooth.gatt.disconnect();
            dispatch(setBluetooth(null));
            setIsConnected(false);
            setReceivedMessages([]);
            message.success('已断开蓝牙连接');
            return true;
        }
        return false;
    }, [bluetooth]);

    // 发送消息
    const sendMessage = useCallback(async (bleData) => {
        if (!bluetooth) {
            console.log('蓝牙设备未连接');
            return false;
        }
        if (!bleData) {
            console.log('请输入要发送的内容');
            return false;
        }
        const bleDataString = JSON.stringify(bleData);
        try {
            const service = await bluetooth.gatt.getPrimaryService(SERVICE_UUID);
            const rxCharacteristic = await service.getCharacteristic(CHARACTERISTIC_UUID_RX);
            const data = new TextEncoder().encode(bleDataString);
            const length = data.length + 1;
            const bytes = new Uint8Array([
                251, 27, 0, length % 256, Math.floor(length / 256), // 消息头
                ...data, 254 // 消息尾
            ]);
            await rxCharacteristic.writeValue(bytes);
            console.log('发送成功', bleData);
            return true;
        } catch (error) {
            console.log('发送数据失败:', error);
            return false;
        }
    }, [bluetooth]);

    // 清空接收到的消息
    const clearMessages = useCallback(() => {
        setReceivedMessages([]);
    }, []);

    const setupNotifications = useCallback(async (callback) => {
        if (bluetooth && txCharacteristic && callback) {
            txCharacteristic.oncharacteristicvaluechanged = (event) => handleCharacteristicValueChanged(event, callback);
            await txCharacteristic.startNotifications();
            console.log('setup-Notifications')
        }
    }, [bluetooth, handleCharacteristicValueChanged]);

    const stopNotifications = useCallback(async (callback) => {
        if (bluetooth && txCharacteristic && callback) {
            txCharacteristic.oncharacteristicvaluechanged = null;
            await txCharacteristic.stopNotifications();
            console.log('stop-Notifications')
        }
    },[handleCharacteristicValueChanged])


    return {
        isConnected,
        receivedMessages, 
        setupNotifications,
        stopNotifications,
        connect,
        disconnect,
        sendMessage,
        clearMessages
    };
};

export default useBluetooth;